import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, Clock, FileCheck2, PartyPopper, ArrowRight, Upload } from 'lucide-react';
import { apiService } from '@/lib/api';
import { money, fmtDate, fmtDateTime, APP_STATUS, LENDER_BLUE, LENDER_NAVY } from '@/lib/lenderDemo';

const EV = {
  submitted: 'Request submitted', docs_requested: 'Lender requested documents',
  docs_uploaded: 'Documents uploaded', approved: 'Loan approved', closed: 'Closed & funded',
};

export default function LenderApplicationStatus() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiService.lenderApplicationDetail(appId).then(({ data }) => setApp(data.application)).catch(() => setApp(false));
  }, [appId]);
  useEffect(() => { load(); }, [load]);

  if (app === false) return <div className="p-10 text-center text-slate-400">Application not found.</div>;
  if (!app) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  const chip = APP_STATUS[app.status] || APP_STATUS.in_progress;
  const required = app.required_docs || [];
  const provided = app.documents || [];
  const isProvided = (k) => provided.some((d) => d.key === k && d.provided);

  const uploadRequested = async () => {
    setBusy(true);
    try {
      const documents = required.map((r) => ({ key: r.key, label: r.label, provided: true, filename: `${r.key}.pdf` }));
      await apiService.lenderAppUploadDocs(appId, { documents });
      load();
    } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F5F9' }}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ background: LENDER_BLUE }}>D</div>
            <span className="font-bold" style={{ color: LENDER_NAVY }}>DrawReady</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Borrower</span>
          </div>
          <button onClick={() => navigate('/lender/demo')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Lender demo
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{app.project_name}</h1>
            <p className="text-slate-500 text-sm">{app.borrower_name} · requested {money(app.requested_amount)}</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="app-status-chip" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
        </div>

        {/* Status banner */}
        {app.status === 'in_progress' && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4 flex items-start gap-3" data-testid="app-banner">
            <Clock className="w-5 h-5 mt-0.5" style={{ color: LENDER_BLUE }} />
            <div><div className="font-semibold" style={{ color: LENDER_NAVY }}>Loan request in progress</div>
              <p className="text-sm text-slate-500">Your lender is reviewing your request. We'll let you know if they need anything else.</p></div>
          </div>
        )}
        {app.status === 'needs_docs' && (
          <div className="bg-white border border-amber-200 rounded-xl p-5 mb-4" data-testid="app-banner">
            <div className="flex items-start gap-3 mb-3">
              <FileCheck2 className="w-5 h-5 mt-0.5 text-amber-500" />
              <div><div className="font-semibold text-amber-800">Your lender needs more documents</div>
                <p className="text-sm text-slate-500">Upload the items below to continue your loan request.</p></div>
            </div>
            <div className="space-y-1.5 mb-3 pl-8">
              {required.map((r) => (
                <div key={r.key} className="flex items-center gap-2 text-sm">
                  {isProvided(r.key) ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-amber-400" />}
                  <span className={isProvided(r.key) ? 'text-slate-600' : 'text-amber-700 font-medium'}>{r.label}</span>
                </div>
              ))}
            </div>
            <button onClick={uploadRequested} disabled={busy} data-testid="app-upload-docs"
              className="ml-8 flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}>
              <Upload className="w-4 h-4" /> {busy ? 'Uploading…' : 'Upload requested documents'}
            </button>
          </div>
        )}
        {app.status === 'approved' && (
          <div className="bg-white border border-emerald-200 rounded-xl p-5 mb-4 flex items-start gap-3" data-testid="app-banner">
            <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-500" />
            <div><div className="font-semibold text-emerald-800">Approved — closing scheduled</div>
              <p className="text-sm text-slate-500">Your loan is approved for {money(app.approved_amount)}. Closing is set for <strong>{fmtDate(app.closing_date)}</strong>. At closing your budget is set up and funds become available to draw.</p></div>
          </div>
        )}
        {app.status === 'closed' && (
          <div className="bg-white border border-emerald-200 rounded-xl p-5 mb-4" data-testid="app-banner">
            <div className="flex items-start gap-3 mb-3">
              <PartyPopper className="w-5 h-5 mt-0.5 text-emerald-500" />
              <div><div className="font-semibold text-emerald-800">Funds available 🎉</div>
                <p className="text-sm text-slate-500">You're closed and funded. Your budget is live in DrawReady — start your project and draw app-to-app from your lender.</p></div>
            </div>
            <button onClick={() => navigate(`/lender/borrower/${app.loan_id}`)} data-testid="app-open-portal"
              className="ml-8 flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}>
              Open your Budget & Progress <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Documents + bid summary */}
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold text-sm mb-2" style={{ color: LENDER_NAVY }}>Documents attached</div>
            {provided.filter((d) => d.provided).map((d) => (
              <div key={d.key} className="flex items-center gap-2 text-sm py-0.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-slate-600">{d.label}</span></div>
            ))}
            {provided.filter((d) => d.provided).length === 0 && <p className="text-sm text-slate-400">None yet.</p>}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold text-sm mb-2" style={{ color: LENDER_NAVY }}>Bid sheet</div>
            <div className="text-sm text-slate-500">{(app.bid_lines || []).length} budget lines · {money(app.requested_amount)} requested</div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="font-semibold text-sm mb-3" style={{ color: LENDER_NAVY }}>Activity</div>
          <ol className="space-y-3">
            {(app.events || []).map((e) => (
              <li key={e.id} className="flex gap-2.5" data-testid={`app-event-${e.type}`}>
                <Circle className="w-3 h-3 mt-1" style={{ color: LENDER_BLUE, fill: LENDER_BLUE }} />
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: LENDER_NAVY }}>{EV[e.type] || e.type}</div>
                  {e.note && <div className="text-xs text-slate-500">{e.note}</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{e.actor_name} · {fmtDateTime(e.created_at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </main>
    </div>
  );
}
